import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AlertCircle, Loader2, Lock } from 'lucide-react';
import api from '../../services/api';
import { successAlert, errorAlert } from '../../utils/alerts';
import AuthLayout from './AuthLayout';

const ResetPassword = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres.');
      return;
    }
    if (password !== password2) {
      setError('As senhas não conferem.');
      return;
    }

    try {
      setLoading(true);
      await api.post('/auth/password-reset-confirm/', {
        uid,
        token,
        password,
        password2,
      });
      await successAlert('Senha redefinida!', 'Faça login com sua nova senha.');
      navigate('/login');
    } catch (err) {
      const data = err.response?.data;
      const message =
        data?.detail ||
        (Array.isArray(data?.password) ? data.password[0] : null) ||
        'Não foi possível redefinir a senha. Tente novamente.';
      // Link inválido/expirado: sugere solicitar um novo
      if (data?.detail) {
        setLinkExpired(true);
      }
      errorAlert('Erro ao redefinir senha', message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Redefinir senha">
      <p className="text-sm text-gray-500 mb-6">
        Defina sua nova senha de acesso.
      </p>

      {error && (
        <div className="mb-5 flex items-start gap-3 bg-red-50/80 border-l-4 border-red-500 p-4 rounded-r-lg text-sm text-red-700 shadow-sm">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {linkExpired && (
        <div className="mb-5 flex items-start gap-3 bg-amber-50/80 border-l-4 border-amber-500 p-4 rounded-r-lg text-sm text-amber-700 shadow-sm">
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <span>
            Link inválido ou expirado?{' '}
            <Link to="/esqueci-senha" className="font-medium underline hover:text-amber-900">
              Solicite um novo link
            </Link>
            .
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
            Nova senha
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              autoComplete="new-password"
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 focus:bg-white transition-all"
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label htmlFor="password2" className="block text-sm font-medium text-gray-700 mb-1.5">
            Confirmar senha
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="password2"
              type="password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              placeholder="Repita a senha"
              autoComplete="new-password"
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 focus:bg-white transition-all"
              disabled={loading}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 px-4 bg-[#1e3a5f] hover:bg-[#162d49] active:bg-[#12253d] text-white font-semibold rounded-xl shadow-md shadow-blue-900/20 hover:shadow-lg hover:shadow-blue-900/30 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Redefinindo...
            </>
          ) : (
            'Redefinir senha'
          )}
        </button>
      </form>

      <div className="mt-6 text-center">
        <Link
          to="/login"
          className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors font-medium"
        >
          Voltar ao login
        </Link>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
